import { authHeaders, ORIGIN } from './client';

/**
 * Submit a player-issued Wheel command. Wraps
 * {@code POST /api/rooms/{roomId}/commands}; server fills in the
 * expectedVersion from the actor's current snapshot before queueing.
 *
 * <p>Caller fire-and-forget: server returns 202 with a command id.
 * Stale-version rejections surface server-side via the journal —
 * the client doesn't see them in v1. Add a per-command result poll
 * if/when the optimistic-concurrency window starts mattering.
 */

export type WheelCommandType =
  | 'JOIN_ROOM'
  | 'LEAVE_ROOM'
  | 'SUBSCRIBE_AUDIENCE'
  | 'UNSUBSCRIBE_AUDIENCE'
  | 'SPIN_PRE_METER'
  | 'REEL_RACE_SPIN'
  | 'ALLOCATE_STAKE'
  | 'COMMIT_LANCE'
  | 'REACTION'
  | 'CHEER'
  | 'SET_STAKE'
  | 'DEPLOY_ITEM'
  | 'CLAIM_ITEM'
  | 'SELECT_SEAT';

export interface WheelCommandResponse {
  commandId: string;
  status: string;
}

export async function submitWheelCommand(
  roomId: string,
  type: WheelCommandType,
  playerId: string,
  args: Record<string, unknown> = {},
): Promise<WheelCommandResponse> {
  const url = `${ORIGIN}/api/rooms/${encodeURIComponent(roomId)}/commands`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
    body: JSON.stringify({ type, playerId, args }),
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(
      `submitWheelCommand failed: ${res.status} ${res.statusText} ${detail}`,
    );
  }
  return (await res.json()) as WheelCommandResponse;
}
